/**
 * Turning what the camera hands back into what we actually store.
 *
 * A phone camera gives roughly 3 MB for a 12 MP photo. At 52 photos on one ACB
 * that is about **156 MB for a single breaker**, and a 24-breaker lineup is near
 * 3 GB if nothing uploads. iOS evicts storage from apps it considers idle, so
 * "the phone deleted the evidence before it uploaded" is a real and
 * unrecoverable failure on a commissioning job.
 *
 * Downscaling to a long edge of 1600 px at JPEG quality 0.7 takes that 3 MB to
 * roughly 400 KB — the same breaker becomes about 20 MB. A CxA needs a nameplate
 * to be legible, not to be 12 megapixels, so nothing of value is lost.
 *
 * The arithmetic is pure and tested here; the canvas work is a thin wrapper
 * around it, because jsdom has no real canvas and a test of a fake one proves
 * nothing.
 */

export interface PhotoQuality {
  /** Longest edge in pixels after downscaling. */
  maxEdge: number;
  /** JPEG quality, 0–1. */
  quality: number;
  /** Files already smaller than this are stored as they are. */
  skipUnderBytes: number;
}

/**
 * Chosen for legibility of a nameplate and a trip-unit display at arm's length,
 * not for the camera's sake.
 */
export const DEFAULT_QUALITY: PhotoQuality = {
  maxEdge: 1600,
  quality: 0.7,
  skipUnderBytes: 400 * 1024,
};

export interface Size {
  width: number;
  height: number;
}

/**
 * The size to draw at: never larger than the original, never longer than
 * `maxEdge` on its long side, aspect ratio kept.
 *
 * Upscaling a small photo would grow the file for no added detail, which is the
 * opposite of the point.
 */
export function targetSize(source: Size, maxEdge: number): Size {
  const { width, height } = source;
  if (width <= 0 || height <= 0) return { width: 0, height: 0 };
  const longest = Math.max(width, height);
  if (longest <= maxEdge) return { width: Math.round(width), height: Math.round(height) };
  const scale = maxEdge / longest;
  return {
    // At least one pixel each way: a 4000×1 panorama must not become 1600×0,
    // which canvas rejects.
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/** Whether the file is in a format the CxA's Windows machine cannot open. */
export function mustConvert(type: string | undefined): boolean {
  return !!type && /heic|heif/i.test(type);
}

/**
 * Whether it is worth re-encoding at all.
 *
 * The format check comes first on purpose: an iPhone HEIC under the size
 * threshold still has to become a JPEG, or the evidence reaches a CxA who
 * cannot open it.
 */
export function shouldCompress(file: { size: number; type?: string }, q: PhotoQuality = DEFAULT_QUALITY): boolean {
  if (mustConvert(file.type)) return true;
  return file.size > q.skipUnderBytes;
}

/** Roughly what a compressed photo will weigh, for a storage estimate before capture. */
export function estimateBytes(source: Size, q: PhotoQuality = DEFAULT_QUALITY): number {
  const { width, height } = targetSize(source, q.maxEdge);
  // ~0.12 bytes per pixel at q0.7 on photographic content, measured on the
  // MY03 evidence pack. Deliberately an estimate: it drives a warning, not a
  // decision to discard anything.
  return Math.round(width * height * 0.12 * (q.quality / 0.7));
}

export class PhotoError extends Error {}

/** The browser pieces this needs, named so a test can stand in for them. */
export interface ImagePipeline {
  decode(blob: Blob): Promise<Size & { close?: () => void; source: CanvasImageSource }>;
  encode(source: CanvasImageSource, size: Size, quality: number): Promise<Blob>;
}

/**
 * Downscales and re-encodes, or returns the original when that would not help.
 *
 * Never throws away the original on failure: if anything here goes wrong the
 * untouched file is stored instead. A photo that is larger than we wanted is a
 * storage problem; a photo that is missing is a site visit.
 */
export async function preparePhoto(
  file: Blob,
  pipeline: ImagePipeline,
  q: PhotoQuality = DEFAULT_QUALITY,
): Promise<{ blob: Blob; compressed: boolean; reason?: string }> {
  if (file.size === 0) throw new PhotoError('the camera returned an empty file');
  if (!shouldCompress(file, q)) return { blob: file, compressed: false, reason: 'already small enough' };

  try {
    const decoded = await pipeline.decode(file);
    const size = targetSize(decoded, q.maxEdge);
    if (size.width === 0 || size.height === 0) {
      return { blob: file, compressed: false, reason: 'could not read the image size' };
    }
    const out = await pipeline.encode(decoded.source, size, q.quality);
    decoded.close?.();
    // Re-encoding does not always shrink things — a small HEIC can grow as JPEG.
    // Keep whichever is smaller, unless the original is a format the CxA cannot
    // open, in which case the JPEG wins on size alone.
    if (!mustConvert(file.type) && out.size >= file.size) {
      return { blob: file, compressed: false, reason: 'recompression would not be smaller' };
    }
    return { blob: out, compressed: true };
  } catch (err) {
    return {
      blob: file,
      compressed: false,
      reason: `stored uncompressed: ${err instanceof Error ? err.message : String(err)}`,
    };
  }
}

/** The real browser implementation. Untested here on purpose — jsdom has no canvas. */
export const browserPipeline: ImagePipeline = {
  async decode(blob) {
    const bitmap = await createImageBitmap(blob);
    return { width: bitmap.width, height: bitmap.height, source: bitmap, close: () => bitmap.close() };
  },
  async encode(source, size, quality) {
    const canvas = document.createElement('canvas');
    canvas.width = size.width;
    canvas.height = size.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new PhotoError('no 2d context');
    ctx.drawImage(source, 0, 0, size.width, size.height);
    const out = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', quality),
    );
    if (!out) throw new PhotoError('canvas produced no image');
    return out;
  },
};

/** Storage pressure, for the warning an engineer needs before it is too late. */
export interface Pressure {
  bytes: number;
  level: 'fine' | 'watch' | 'urgent';
  message?: string;
}

/**
 * Thresholds are deliberately conservative because the platform that evicts is
 * the one we cannot see doing it. Warning early costs an engineer nothing;
 * warning late costs a day's evidence.
 */
export function pressureOf(bytesHeld: number, pendingPhotos: number): Pressure {
  const mb = bytesHeld / (1024 * 1024);
  if (mb >= 400) {
    return {
      bytes: bytesHeld,
      level: 'urgent',
      message: `${Math.round(mb)} MB of photos are only on this phone. Get signal and let them upload before taking more.`,
    };
  }
  if (mb >= 150 || pendingPhotos >= 200) {
    return {
      bytes: bytesHeld,
      level: 'watch',
      message: `${pendingPhotos} photos (${Math.round(mb)} MB) are waiting to upload.`,
    };
  }
  return { bytes: bytesHeld, level: 'fine' };
}
